/**
 * Profile — self-service profile editor.
 *
 * Now exposes:
 *   - Avatar (layered: uploaded → LinkedIn-pulled → Gravatar → initials)
 *   - Avatar upload (data-URL, saved to `avatar_data_url`)
 *   - Name
 *   - Phone with country-code picker (auto-detected, Qatar fallback)
 *   - LinkedIn URL with the same auto-fill button as the CRM contacts
 *
 * Everything that lands on `/auth/me` goes through `PUT /api/auth/me`.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Upload, Trash2, Phone as PhoneIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import LinkedInButton from '@/components/LinkedInButton';
import LinkedInAutofillField from '@/components/LinkedInAutofillField';
import Avatar from '@/components/Avatar';
import PhoneInput from '@/components/PhoneInput';
import PushNotificationsToggle from '@/components/PushNotificationsToggle';
import SignInActivityPanel from '@/components/SignInActivityPanel';
import ActiveSessionsPanel from '@/components/ActiveSessionsPanel';

const MAX_AVATAR_BYTES = 2 * 1024 * 1024;  // 2 MB

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + form-sync effects. */
export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState(null);
  const [picture, setPicture] = useState(null);       // LinkedIn-pulled URL
  const [avatarUpload, setAvatarUpload] = useState(null);  // local data-URL
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setName(user?.name || '');
    setLinkedinUrl(user?.linkedin_url || '');
    setPhone(user?.phone || '');
    setPhoneCountry(user?.phone_country || null);
    setPicture(user?.picture || null);
    setAvatarUpload(user?.avatar_data_url || null);
  }, [user]);

  const handleAutofill = (fields) => {
    // Use the LinkedIn auto-fill to suggest the user's own name + picture
    // if either field is blank.
    if (!name && fields.name) setName(fields.name);
    if (!picture && fields.picture_url) setPicture(fields.picture_url);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast.error('Pick an image file.');
    if (f.size > MAX_AVATAR_BYTES) return toast.error('Image must be ≤ 2 MB.');
    const reader = new FileReader();
    reader.onload = () => setAvatarUpload(reader.result);
    reader.onerror = () => toast.error('Could not read file.');
    reader.readAsDataURL(f);
  };

  const clearAvatar = () => {
    setAvatarUpload(null);
    setPicture(null);
  };

  const save = async () => {
    // Mirror the backend rule: phone (if provided) must have ≥7 digits.
    if (phone && phone.trim()) {
      const digits = phone.replace(/[^\d]/g, '');
      if (digits.length < 7) {
        toast.error('Phone must contain at least 7 digits.');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        linkedin_url: linkedinUrl.trim() || null,
        phone: phone.trim() || null,
        phone_country: phoneCountry || null,
        picture: picture || null,
        avatar_data_url: avatarUpload || null,
      };
      await api.put('/auth/me', payload);
      toast.success('Profile updated.');
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  // Avatar resolution priority: uploaded → LinkedIn-pulled → Gravatar → initials
  const avatarSrc = avatarUpload || picture || null;

  return (
    <div className="max-w-2xl" data-testid="profile-page">
      <Link to="/dashboard" className="ve-btn-ghost text-xs">
        <ArrowLeft size={12}/> Back to dashboard
      </Link>
      <div className="ve-eyebrow mt-3">Your profile</div>
      <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
        Your details. <span className="ve-gold-text">One place.</span>
      </h1>
      <p className="text-sm text-slate-600 mt-2 max-w-xl">
        Update your name, photo, phone, and LinkedIn URL. Your LinkedIn link appears next to your name across the platform so colleagues can connect in one click.
      </p>

      <div className="mt-6 ve-card p-6 space-y-5">
        {/* Avatar block */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            Photo
          </div>
          <div className="mt-3 flex items-center gap-4">
            <Avatar
              src={avatarSrc}
              email={user?.email}
              name={name || 'You'}
              size={80}
              testId="profile-avatar"
            />
            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onFile}
                className="hidden"
                data-testid="profile-avatar-file"
              />
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="profile-avatar-upload-btn"
                className="ve-btn-secondary text-xs"
              >
                <Upload size={12}/> Upload photo
              </button>
              {(avatarUpload || picture) && (
                <button
                  onClick={clearAvatar}
                  data-testid="profile-avatar-clear-btn"
                  className="text-[10px] uppercase tracking-[0.18em] text-rose-500 hover:text-rose-700 inline-flex items-center gap-1"
                >
                  <Trash2 size={11}/> Remove photo
                </button>
              )}
              <p className="text-[10px] text-slate-500 leading-snug max-w-[18rem]">
                If empty, we&apos;ll use your LinkedIn photo or your Gravatar.
                Max 2 MB.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            Identity
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Full name</div>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="profile-name-input"
                className="w-full rounded-md border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#8B3FC5]"
              />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Email</div>
              <input
                value={user?.email || ''}
                disabled
                className="w-full rounded-md border border-slate-200 px-3 py-2 bg-slate-50 text-slate-500 cursor-not-allowed"
              />
            </label>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold flex items-center gap-2">
            <PhoneIcon size={11} className="text-[#5E1F8B]"/> Phone
          </div>
          <p className="text-xs text-slate-500 mt-1">
            We&apos;ll use this number for meeting reminders and one-tap callbacks.
          </p>
          <div className="mt-3 max-w-md">
            <PhoneInput
              value={phone}
              country={phoneCountry}
              onChange={(p, iso) => { setPhone(p); setPhoneCountry(iso); }}
              testId="profile-phone"
            />
          </div>
        </div>

        <div className="border-t border-slate-200 pt-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold flex items-center gap-2">
            LinkedIn
            <LinkedInButton url={linkedinUrl} size={11}/>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Paste your LinkedIn URL. We can also pull your name and photo from it.
          </p>
          <div className="mt-3">
            <LinkedInAutofillField
              value={linkedinUrl}
              onChange={setLinkedinUrl}
              onAutofill={handleAutofill}
              testId="profile-linkedin"
            />
          </div>
        </div>

        <div className="flex justify-end pt-3 border-t border-slate-200">
          <button
            onClick={save}
            disabled={saving}
            data-testid="profile-save-btn"
            className="ve-btn-primary disabled:opacity-50"
          >
            <Save size={14}/> {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>

        {/* ── Push notifications — per-device opt-in ── */}
        <div className="pt-4 border-t border-slate-200 dark:border-white/10">
          <PushNotificationsToggle />
        </div>

        {/* ── Active Devices — per-device revoke ── */}
        <ActiveSessionsPanel />

        {/* ── Account-security — recent activity + revoke all ── */}
        <SignInActivityPanel />
      </div>
    </div>
  );
}
